import type { BuildingBlockSelectionSwitchNotice } from '@/components/building-block-form/utils/switchValidation'
import type { BuildingBlockDto } from '@/stores/buildingBlock'

export type Category = {
  id: number
  name: string
  blocks: BuildingBlockDto[]
}

export type SelectedBlock = BuildingBlockDto & {
  uniqueKey: string
  categoryId: number
  categoryName: string
}

const DEFAULT_CATEGORY_ID = 0
const CUSTOM_CATEGORY_ID = 1
const DEFAULT_CATEGORY_NAME = 'Default'
const CUSTOM_CATEGORY_NAME = 'Custom Blocks'

const getCategoryMeta = (isHidden: boolean) => {
  return isHidden
    ? { categoryId: DEFAULT_CATEGORY_ID, categoryName: DEFAULT_CATEGORY_NAME }
    : { categoryId: CUSTOM_CATEGORY_ID, categoryName: CUSTOM_CATEGORY_NAME }
}

export const buildAvailableCategories = (calculationBlocks: BuildingBlockDto[]): Category[] => {
  const availableBlocks = calculationBlocks.filter((block) => !block.selected)
  const defaultBlocks = availableBlocks.filter((block) => block.hidden)
  const customBlocks = availableBlocks.filter((block) => !block.hidden)

  const categories: Category[] = []

  if (defaultBlocks.length > 0) {
    categories.push({
      id: DEFAULT_CATEGORY_ID,
      name: DEFAULT_CATEGORY_NAME,
      blocks: defaultBlocks
    })
  }

  if (customBlocks.length > 0) {
    categories.push({
      id: CUSTOM_CATEGORY_ID,
      name: CUSTOM_CATEGORY_NAME,
      blocks: customBlocks
    })
  }

  return categories
}

export const buildSelectedBlocks = (calculationBlocks: BuildingBlockDto[]): SelectedBlock[] => {
  return calculationBlocks
    .filter((block) => block.selected)
    .map((block) => {
      const categoryMeta = getCategoryMeta(Boolean(block.hidden))
      return {
        ...block,
        uniqueKey: `${categoryMeta.categoryId}-${block.id}`,
        categoryId: categoryMeta.categoryId,
        categoryName: categoryMeta.categoryName
      }
    })
}

export const resolveSelectionNoticeTitle = (
  notice: BuildingBlockSelectionSwitchNotice | null | undefined
): string => {
  const base = notice?.baseDatabaseName
  const target = notice?.targetDatabaseName
  if (base && target) {
    return `Some default building blocks were affected after switching database from ${base} to ${target}.`
  }
  return 'Some default building blocks were affected after database switch.'
}

export const buildSelectionMissingMessages = (
  notice: BuildingBlockSelectionSwitchNotice | null | undefined
): string[] => {
  const missingBlocks = notice?.missingBlocks ?? []
  return missingBlocks.map((item) => {
    const materials = item.materialNames.join(', ')
    return `Removed building block ${item.blockName}. Missing materials: ${materials}.`
  })
}

export const buildSelectionChangedMessages = (
  notice: BuildingBlockSelectionSwitchNotice | null | undefined
): string[] => {
  const changedBlocks = notice?.changedBlocks ?? []
  return changedBlocks.map((item) => {
    const materials = item.materialNames.join(', ')
    return `Updated values detected for building block ${item.blockName}. Affected materials: ${materials}.`
  })
}
